/**
 * list-column-set.ts — JEDNO źródło prawdy: „kolumny, które lista kartoteki umie pokazać".
 *
 * Zbiór składa się z trzech części:
 *  - katalog pól kartoteki (`ReferenceListContext.availableFields`),
 *  - built-iny `record_name` / `record_number` — tylko tam, gdzie są prawdziwymi kolumnami
 *    (`builtinColumnsAvailable`: moduł własny / szablon, encja `new_table`),
 *  - kolumny rysowane przez STRONĘ listy spoza katalogu (Firma, Kategoria, szczegóły umowy,
 *    Nadawca, Konto…) — z rejestru `list-column-registry` (`getRegisteredColumnIds`).
 *
 * Korzystają z niego: zakładka „Kolumny" (`ListColumnsPanel` — paleta „Dodaj kolumnę" i odsiew
 * kolumn-sierot przy zapisie) oraz zakładka „Kalendarz" (`ListCalendarPanel` — kandydaci na pole
 * statusu i etykiety). Dwóch konsumentów, jedna definicja — kalendarz nie może oferować czegoś
 * innego, niż lista umie pokazać.
 */

import {
  kartotekaExcludedListFieldSlugs,
  kartotekaListFieldLabelKey,
  replacementColumnId,
} from '@saas/shared';

import type { ReferenceListContextValue } from '@/contexts/ReferenceListContext';
import { parseTemplateTableId } from '@/lib/module-templates/template-layouts';

import { getRegisteredColumnIds } from './list-column-registry';

/** Jedna kolumna, którą lista umie pokazać: `id` (zapisywane w układzie / `calendarConfig`) i nazwa. */
export interface ListColumnCandidate {
  id: string;
  name: string;
}

export interface ListColumnSet {
  /**
   * Id DOZWOLONE: katalog pól ∪ built-iny ważne w tym kontekście ∪ wszystkie id z rejestru
   * (także w trybie szablonu — tam rejestr nie jest oferowany w palecie, ale jego id nie są
   * sierotami). Podstawa odsiewu kolumn-sierot przy zapisie układu.
   */
  allowedIds: ReadonlySet<string>;
  /**
   * Kandydaci do palety „Dodaj kolumnę" i list wyboru pola, w kolejności: built-iny, katalog
   * (bez built-inów — te mają pierwszeństwo), kolumny rejestru spoza katalogu. Deduplikacja po `id`.
   * Kolumny rejestru nie są oferowane w trybie szablonu (kreator listy w panelu ITE jeszcze ich nie
   * obsługuje — etykiety i akceptacja serwera dojdą z kreatorem listy obcej kartoteki).
   */
  candidates: readonly ListColumnCandidate[];
  /**
   * Pola katalogu, które lista klienta rysuje WŁASNĄ kolumną z rejestru (np. `company_id` → kolumna
   * `company`, `category_id` → `category`) — z `kartotekaExcludedListFieldSlugs`, tej samej tabeli,
   * z której korzysta kreator listy w panelu ITE. Ta sama kolumna pod dwoma id (pole katalogu i
   * kolumna rejestru) jest w `candidates` obecna dwa razy; konsument, który chce ją pokazać RAZ
   * (zakładka „Kalendarz"), pomija te id i zostawia kolumnę rejestru — to jej id zapisują układy.
   * Pusty w trybie szablonu (rejestr nie jest wtedy oferowany).
   */
  replacedCatalogIds: ReadonlySet<string>;
  /**
   * Id kanoniczne kolumny: pole katalogu zastąpione kolumną rejestru (`company_id` → `company`,
   * `category_id` → `category`) zamienia się na id kolumny — tą samą regułą co normalizacja układów
   * listy (`replacementColumnId` z `@saas/shared`). Zamiana tylko wtedy, gdy kolumna zastępcza jest
   * wśród `candidates`; każde inne id (także nieznane) wraca bez zmian — nic nie jest kasowane.
   */
  canonicalColumnId: (id: string) => string;
}

/**
 * Składa zbiór kolumn listy z kontekstu kreatora. `undefined`, gdy katalog pól jest nieznany
 * (`availableFields` niezdefiniowane — standalone, nieznany kontekst albo katalog się jeszcze
 * ładuje): brak podstawy do filtrowania i do oferowania czegokolwiek.
 *
 * `columnLabel` = `useColumnLabel(slug)` wołającego (nazwy kolumn rejestru; nieznane → surowe id),
 * `t` = `useTranslation().t` (nazwy built-inów).
 */
export function resolveListColumnSet(args: {
  slug: string;
  referenceCtx: ReferenceListContextValue | null;
  columnLabel: (columnId: string) => string | undefined;
  t: (key: string) => string;
}): ListColumnSet | undefined {
  const { slug, referenceCtx, columnLabel, t } = args;
  const availableFields = referenceCtx?.availableFields;
  if (!availableFields) return undefined;

  // Built-iny record_name/record_number są PRAWDZIWYMI kolumnami WYŁĄCZNIE w kontekście modułu
  // własnego / szablonu (encja new_table — fizyczna kolumna nazwy/numeru rekordu). Dla kartotek
  // systemowych data-driven (towary, kontrahenci, dokumenty, pracownicy) i dla encji-odwołania do
  // kartoteki systemowej NIE istnieją — sygnał z kontekstu (ustawiany przez providera osadzającego
  // builder), domyślnie false (bezpieczny fallback).
  const builtins: ListColumnCandidate[] = referenceCtx?.builtinColumnsAvailable
    ? [
        {
          id: 'record_name',
          name: t('moduleTemplateEditor.layoutDesigner.builtinColumns.recordName'),
        },
        {
          id: 'record_number',
          name: t('moduleTemplateEditor.layoutDesigner.builtinColumns.recordNumber'),
        },
      ]
    : [];

  // Slug rejestru: kontekst (menu-embed, encja-odwołanie) ma priorytet — `slug` w tym trybie jest
  // `template:<code>:<entitySlug>:<viewSlug>` (bez `::`), więc split poniżej zwróciłby cały string
  // i rejestr nigdy by nie trafił (patrz JSDoc `columnRegistrySlug`). Układ wymuszony pozycją menu
  // ma postać `<slug>::layout-<id>` — rejestr jest kluczowany czystym slugiem modułu.
  const registrySlug = referenceCtx?.columnRegistrySlug ?? slug.split('::')[0] ?? slug;
  const registeredIds = getRegisteredColumnIds(registrySlug);

  const builtinIds = new Set(builtins.map((b) => b.id));
  const templateMode = parseTemplateTableId(slug) !== null;
  // Pole katalogu, które lista klienta podpisuje inaczej niż katalog (pracownicy: `last_name` =
  // złożona kolumna „Imię i nazwisko", obok osobnej kolumny `surname` „Nazwisko" z rejestru) —
  // nazwa jak nagłówek kolumny na liście, spis w `@saas/shared`. Tylko tam, gdzie oferowany jest
  // rejestr kolumn strony: w trybie szablonu (panel ITE) nazwy zostają z katalogu.
  const catalogName = (f: { id: string; name: string }): string => {
    const labelKey = templateMode ? undefined : kartotekaListFieldLabelKey(registrySlug, f.id);
    return labelKey ? t(labelKey) : f.name;
  };
  const catalogCandidates: ListColumnCandidate[] = availableFields
    .filter((f) => !builtinIds.has(f.id))
    .map((f) => ({ id: f.id, name: catalogName(f) }));
  const knownIds = new Set([...builtinIds, ...catalogCandidates.map((f) => f.id)]);
  const registryCandidates: ListColumnCandidate[] = templateMode
    ? []
    : registeredIds
        .filter((id) => !knownIds.has(id))
        .map((id) => ({ id, name: columnLabel(id) ?? id }));

  const candidates: ListColumnCandidate[] = [
    ...builtins,
    ...catalogCandidates,
    ...registryCandidates,
  ];
  const candidateIds = new Set(candidates.map((c) => c.id));
  // Tylko pola wykluczone, które MAJĄ kolumnę zastępczą (`company_id` → `company`…). Pola umowy
  // (ADR-0109b) też są wykluczone z listy, ale bez kolumny zastępczej — a trzy z nich mają to samo
  // id co kolumna strony (`contract_number`/`contract_status`/`contract_value`), której zakładka
  // „Kalendarz" nie może schować.
  const replacedCatalogIds: ReadonlySet<string> =
    registryCandidates.length > 0
      ? new Set(
          kartotekaExcludedListFieldSlugs(registrySlug).filter(
            (fieldSlug) => replacementColumnId(registrySlug, fieldSlug) !== null,
          ),
        )
      : new Set<string>();

  return {
    allowedIds: new Set<string>([
      ...availableFields.map((f) => f.id),
      ...builtinIds,
      ...registeredIds,
    ]),
    candidates,
    replacedCatalogIds,
    canonicalColumnId: (id) => {
      if (!replacedCatalogIds.has(id)) return id;
      const replacement = replacementColumnId(registrySlug, id);
      return replacement !== null && candidateIds.has(replacement) ? replacement : id;
    },
  };
}
